import React, { useState, useEffect, useRef, useContext } from "react";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import CustomInput from "../../components/CustomInput";
import PageWrapper from "../../components/PageWrapper";
import axios from "axios";
import { useTranslation } from "react-i18next";
import DiscountSection from "../../components/DiscountSection";
import CustomButton from "../../components/CustomButton";
import SectionDivider from "../../components/SectionDivider";
import { useReactToPrint } from "react-to-print";
import CashierReceipt from "./CashierReceipt";
import { v4 as uuidv4 } from "uuid";
import { UserAuth } from "../../utils/AuthContext";

const INVENTORY_KEY = "inventory_data";

const Cashier = () => {
  const { userData } = useContext(UserAuth);

  const { t } = useTranslation("general");
  const [cart, setCart] = useState([]);
  const [scannedCode, setScannedCode] = useState("");
  const [originalPrice, setOriginalPrice] = useState(0);
  const [netPrice, setNetPrice] = useState(0);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const invoiceRef = useRef(null);

  useEffect(() => {
    setInvoiceNumber(Math.floor(10000 + Math.random() * 90000).toString());

    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0");
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const year = today.getFullYear();
    setInvoiceDate(`${day}/${month}/${year}`);
  }, []);

  const cartTable = {
    titles: [
      { value: t("barcode") },
      { value: t("name") },
      { value: t("price") },
      { value: t("quantity") },
      { value: t("totalAmount") },
    ],
    rows: cart.map((item) => [
      { value: item.barcode, type: TABLE_CELL_TYPES.TEXT },
      { value: item.name, type: TABLE_CELL_TYPES.TEXT },
      {
        value: `${item?.sellingPriceCurrency} ${item.price}`,
        type: TABLE_CELL_TYPES.TEXT,
      },
      { value: item.quantity, type: TABLE_CELL_TYPES.TEXT },
      { value: item.quantity * item.price, type: TABLE_CELL_TYPES.TEXT },
    ]),
  };

  const getProduct = async () => {
    const token = localStorage.getItem("token");

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getProduct/${scannedCode}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Organization-Id": userData?.organizationId,
          },
        }
      );

      if (response.data.success && response.data.product !== null) {
        const product = response?.data?.product;
        const existingItem = cart.find((item) => item.id === product.id);

        if (existingItem) {
          setCart((prevItems) =>
            prevItems.map((item) =>
              item.id === product.id
                ? { ...item, quantity: item.quantity + 1 }
                : item
            )
          );
        } else {
          setCart((prevItems) => [...prevItems, { ...product, quantity: 1 }]);
        }
      } else {
        console.log("Failed to fetch product");
      }
    } catch (error) {
      console.error("Error fetching product by barcode:", error);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: invoiceRef,
    onAfterPrint: () => console.log("Printing completed"),

    pageStyle: `
      @page {
        size: 80mm 100mm;
        margin: 0;
      }
      @media print {
        body {
          width: 80mm;
          margin: 0;
          padding: 0;
          font-family: Arial, sans-serif;
        }
      }
    `,
  });

  const finishThePayment = async () => {
    if (cart.length === 0) {
      alert("Cart is empty!");
      return;
    }

    // Here you would typically send the transaction to your backend
    const token = localStorage.getItem("token");
    try {
      const payload = {
        items: cart,
        total: netPrice,
        invoiceNumber,
        date: invoiceDate,
      };
      // Uncomment when your backend is ready
      // const response = await axios.post(`${process.env.REACT_APP_URL}/createTransaction`, payload, {
      //   headers: {
      //     Authorization: `Bearer ${token}`,
      //   },
      // });

      // Clear cart after successful payment
      // setCart([]);
      // setOriginalPrice(0);
      // setNetPrice(0);

      handlePrint();
      // Generate new invoice number for next transaction
      setInvoiceNumber(Math.floor(10000 + Math.random() * 90000).toString());
    } catch (error) {
      console.error("Error creating transaction:", error);
    }
  };

  useEffect(() => {
    const total = cart.reduce(
      (sum, item) => sum + item.quantity * item.price,
      0
    );
    setOriginalPrice(total);
    setNetPrice(total);
  }, [cart]);

  const handlePriceChange = (newPrice) => {
    setNetPrice(newPrice);
  };

  return (
    <PageWrapper>
      <div className="o-column o-column--gap-size_300 u-padding--bottom_500">
        <h2>🛒 {t("cashierSystem")}</h2>
        <CustomInput
          type="text"
          placeholder={t("scanBarcode")}
          value={scannedCode}
          onChange={(e) => setScannedCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              getProduct();
              setScannedCode("");
            }
          }}
        />
        <RoundedTable {...cartTable} />
      </div>

      <SectionDivider title={t("paymentDetails")} />
      <DiscountSection
        originalPrice={originalPrice}
        onPriceChange={handlePriceChange}
        inputMaxWidth="u-max-width_500"
      />
      <div className="o-row o-row--horizontal-align_end u-padding--bottom_600 u-margin--top_500">
        <CustomButton title={t("pay")} onClick={finishThePayment} />
      </div>

      {/* Hidden invoice for printing */}
      <CashierReceipt
        ref={invoiceRef}
        invoiceNumber={invoiceNumber}
        invoiceDate={invoiceDate}
        cart={cart}
        netPrice={netPrice}
        originalPrice={originalPrice}
      />
    </PageWrapper>
  );
};

export default Cashier;
